import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';

const CreateContentTrack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams(); // Get orgId from route if creating from org page
  const { currentUser, userData, isSuperAdmin } = useAuth();
  const isChildrenPreset = new URLSearchParams(location.search).get('type') === 'children';
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(orgId || ''); // Auto-select if orgId in route
  const [loading, setLoading] = useState(true);

  const [track, setTrack] = useState({
    name: '',
    description: '',
    trackType: isChildrenPreset ? 'children' : 'standard',
    targetAgeRange: '',
    visibility: {
      isPublic: true, // Default to public when no org is selected
      orgOnly: false
    },
    maturityLevels: {
      blue: false,
      green: false,
      yellow: false
    },
    missionalScale: {
      min: -10,
      max: 9
    }
  });

  useEffect(() => {
    const fetchUserOrgs = async () => {
      if (!currentUser) {
        console.log('No current user found');
        return;
      }
      
      try {
        console.log('Fetching orgs for user:', currentUser.uid);
        
        // Get user's organizations subcollection
        const userOrgsRef = collection(db, 'users', currentUser.uid, 'organizations');
        const userOrgsSnapshot = await getDocs(userOrgsRef);
        
        console.log('Raw user orgs data:', userOrgsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        })));

        // Get full organization details for each org
        const orgsData = await Promise.all(
          userOrgsSnapshot.docs.map(async (orgDoc) => {
            const fullOrgDoc = await getDoc(doc(db, 'organizations', orgDoc.id));
            const orgData = fullOrgDoc.data();
            const membershipData = orgDoc.data();

            return {
              id: orgDoc.id,
              name: orgData?.name || orgData?.displayName,
              role: membershipData?.role,
              isAdmin: membershipData?.role === 'Admin',
              status: membershipData?.status
            };
          })
        );

        console.log('Processed organizations:', orgsData);

        // Filter for active organizations where user is admin
        const activeAdminOrgs = orgsData.filter(org => 
          org.status === 'active' && org.isAdmin
        );

        console.log('Active admin organizations:', activeAdminOrgs);
        
        setOrganizations(activeAdminOrgs);
        
        // If orgId is in route, use it and set to org-specific
        if (orgId && activeAdminOrgs.find(org => org.id === orgId)) {
          setSelectedOrg(orgId);
          setTrack(prev => ({
            ...prev,
            visibility: {
              isPublic: false,
              orgOnly: true
            }
          }));
        } else if (activeAdminOrgs.length === 1) {
          // Auto-select if only one org
          const singleOrgId = activeAdminOrgs[0].id;
          setSelectedOrg(singleOrgId);
          // When org is auto-selected, set to org-only
          setTrack(prev => ({
            ...prev,
            visibility: {
              isPublic: false,
              orgOnly: true
            }
          }));
        } else {
          // No org selected, keep public default
          setTrack(prev => ({
            ...prev,
            visibility: {
              isPublic: true,
              orgOnly: false
            }
          }));
        }

      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserOrgs();
  }, [currentUser]);

  const isChildrenTrack = track.trackType === 'children';

  useEffect(() => {
    if (isChildrenTrack) {
      // Clear maturity and missional settings for children's tracks
      setTrack(prev => ({
        ...prev,
        maturityLevels: { blue: false, green: false, yellow: false },
        missionalScale: { min: -10, max: 9 }
      }));
    }
  }, [isChildrenTrack]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTrack(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Organization is optional - only required if orgOnly is true
    if (track.visibility.orgOnly && !selectedOrg) {
      alert('Please select an organization for organization-only tracks');
      return;
    }

    try {
      // Determine approval status - super admins don't need approval
      const needsApproval = !isSuperAdmin();
      const approvalStatus = needsApproval ? 'pending' : 'approved';
      
      const selectedOrgName = selectedOrg
        ? (organizations.find(org => org.id === selectedOrg)?.name || '')
        : '';

      const trackData = {
        ...(selectedOrg && { organizationId: selectedOrg, organizationName: selectedOrgName }),
        name: track.name,
        description: track.description,
        trackType: track.trackType || 'standard', // Always include trackType
        ...(isChildrenTrack && track.targetAgeRange
          ? { targetAgeRange: track.targetAgeRange }
          : {}),
        visibility: {
          isPublic: track.visibility.isPublic,
          orgOnly: track.visibility.orgOnly
        },
        ...(!isChildrenTrack ? {
          targetAudience: {
            maturityLevels: {
              blue: track.maturityLevels.blue,
              green: track.maturityLevels.green,
              yellow: track.maturityLevels.yellow
            },
            missionalScale: {
              min: track.missionalScale.min,
              max: track.missionalScale.max
            }
          }
        } : {}),
        status: 'draft',
        approvalStatus: approvalStatus, // 'pending', 'approved', or 'rejected'
        createdBy: currentUser.uid,
        dateCreated: serverTimestamp(),
        lastModified: serverTimestamp(),
        contentPieces: []
      };

      const docRef = await addDoc(collection(db, 'contentTracks'), trackData);
      console.log('Track created with ID:', docRef.id);
      
      // Navigate based on whether this is org-specific or global
      if (orgId) {
        navigate(`/organizations/${orgId}/content-tracks`);
      } else {
        navigate(`/content-tracks/${docRef.id}/edit`);
      }
      
    } catch (error) {
      console.error('Error creating track:', error);
      alert('Failed to create track. Please try again.');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">
          {isChildrenPreset ? "Create Children's Content Track" : "Create New Content Track"}
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Organization Selector - Optional */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Organization {!orgId && <span className="text-gray-500 font-normal text-sm">(Optional)</span>}
            </label>
            {orgId ? (
              // When accessed from org route, show as read-only
              <div>
                <input
                  type="text"
                  value={organizations.find(org => org.id === orgId)?.name || 'Loading...'}
                  disabled
                  className="w-full p-2 border rounded bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This track will be created for this organization. The organization cannot be changed.
                </p>
              </div>
            ) : (
              // When accessed from global route, allow selection
              <>
                <select
                  value={selectedOrg || ''}
                  onChange={(e) => {
                    const newOrgId = e.target.value || null;
                    setSelectedOrg(newOrgId);
                    
                    // If no organization selected, automatically set to public
                    if (!newOrgId) {
                      setTrack(prev => ({
                        ...prev,
                        visibility: {
                          isPublic: true,
                          orgOnly: false
                        }
                      }));
                    } else {
                      // If organization selected, default to org-only
                      setTrack(prev => ({
                        ...prev,
                        visibility: {
                          isPublic: false,
                          orgOnly: true
                        }
                      }));
                    }
                  }}
                  className="w-full p-2 border rounded"
                >
                  <option value="">No organization (Public track)</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedOrg 
                    ? 'This track will be available to your organization members. You can make it public in visibility settings.'
                    : 'This track will be available to all users if marked as public, or you can restrict it to your organization later.'}
                </p>
              </>
            )}
          </div>

          {/* Track Type */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Track Type</label>
            <select
              name="trackType"
              value={track.trackType}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="standard">Standard</option>
              <option value="children">Children's</option>
            </select>
          </div>

          {/* Target Age Range - only for children */}
          {isChildrenTrack && (
            <div>
              <label className="block text-gray-700 font-bold mb-2">Target Age Range</label>
              <input
                type="text"
                name="targetAgeRange"
                value={track.targetAgeRange}
                onChange={handleInputChange}
                placeholder="e.g., 3-5, 6-8, 9-12"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          )}


          {/* Basic Information */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Track Name
            </label>
            <input
              type="text"
              value={track.name}
              onChange={(e) => setTrack(prev => ({
                ...prev,
                name: e.target.value
              }))}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Description
            </label>
            <textarea
              value={track.description}
              onChange={(e) => setTrack(prev => ({
                ...prev,
                description: e.target.value
              }))}
              className="w-full p-2 border rounded"
              rows="4"
              required
            />
          </div>

          {/* Visibility Settings */}
          <div>
            <h3 className="font-bold mb-2">Visibility Settings</h3>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who can access this track?
              </label>
              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="radio"
                    name="visibility"
                    value="public"
                    checked={track.visibility.isPublic === true}
                    onChange={(e) => setTrack(prev => ({
                      ...prev,
                      visibility: {
                        isPublic: true,
                        orgOnly: false
                      }
                    }))}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-700">Public</span>
                    <p className="text-xs text-gray-500">Available to all TOM users</p>
                  </div>
                </label>
                
                {selectedOrg && (
                  <label className="flex items-start">
                    <input
                      type="radio"
                      name="visibility"
                      value="orgSpecific"
                      checked={track.visibility.isPublic === false && track.visibility.orgOnly === true}
                      onChange={(e) => setTrack(prev => ({
                        ...prev,
                        visibility: {
                          isPublic: false,
                          orgOnly: true
                        }
                      }))}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-700">Organization Specific</span>
                      <p className="text-xs text-gray-500">Available only to your organization members</p>
                    </div>
                  </label>
                )}
              </div>
              {!isSuperAdmin() && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This track will require super admin approval before it can be published.
                  </p>
                </div>
              )}
            </div>
          </div>

          {!isChildrenTrack && (
            <>
              {/* Maturity Levels */}
              <div>
                <h3 className="font-bold mb-2">Maturity Levels</h3>
                <div className="space-y-2">
                  {Object.keys(track.maturityLevels).map(level => (
                    <label key={level} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={track.maturityLevels[level]}
                        onChange={(e) => setTrack(prev => ({
                          ...prev,
                          maturityLevels: {
                            ...prev.maturityLevels,
                            [level]: e.target.checked
                          }
                        }))}
                        className="mr-2"
                      />
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Missional Scale */}
              <div>
                <h3 className="font-bold mb-2">Missional Scale Range</h3>
                <div className="flex space-x-4">
                  <div>
                    <label className="block text-sm">Min</label>
                    <input
                      type="number"
                      min="-10"
                      max="9"
                      value={track.missionalScale?.min ?? -10}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) {
                          setTrack(prev => ({
                            ...prev,
                            missionalScale: {
                              ...prev.missionalScale,
                              min: val
                            }
                          }));
                        }
                      }}
                      className="w-20 p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm">Max</label>
                    <input
                      type="number"
                      min="-10"
                      max="9"
                      value={track.missionalScale?.max ?? 9}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) {
                          setTrack(prev => ({
                            ...prev,
                            missionalScale: {
                              ...prev.missionalScale,
                              max: val
                            }
                          }));
                        }
                      }}
                      className="w-20 p-2 border rounded"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="bg-[#5ABA47] text-white px-6 py-2 rounded hover:bg-[#4a9939]"
          >
            Create Track
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default CreateContentTrack; 